import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Undo2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import type { Statement, StatementDepositOutcome } from '@app/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { IconActionButton } from '@/components/ui/icon-action-button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/ui/table-pagination';
import { usePagination } from '@/hooks/use-pagination';
import { apiErrorMessage } from '@/lib/api';
import { formatCents, formatIsoDate } from '@/lib/format';
import { AssignmentTabs } from './AssignmentTabs';
import { useCancelStatement, useDepositStatements, useStatements } from './statementsQueries';

/** Limites du serveur (`@fastify/multipart`) : 20 fichiers de 20 Mo au plus par dépôt. */
const MAX_FILES = 20;
const MAX_BYTES = 20 * 1024 * 1024;

const isAccepted = (file: File) => /\.(pdf|md)$/i.test(file.name);

function OutcomeLine({ outcome }: { outcome: StatementDepositOutcome }) {
  switch (outcome.outcome) {
    case 'processed':
      return (
        <li className="flex flex-wrap items-center gap-2">
          <Badge>Importé</Badge>
          <span className="font-medium">{outcome.fileName}</span>
          <span className="text-muted-foreground">
            {outcome.statement.transactionsCreated} écriture(s) créée(s)
            {outcome.statement.transactionsSkipped > 0 && `, ${String(outcome.statement.transactionsSkipped)} déjà connue(s)`}
          </span>
        </li>
      );
    case 'rejected':
      return (
        <li className="flex flex-wrap items-center gap-2">
          <Badge variant="destructive">Rejeté</Badge>
          <span className="font-medium">{outcome.fileName}</span>
          <span className="text-destructive">{outcome.reason}</span>
        </li>
      );
    case 'duplicate':
      return (
        <li className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">Déjà déposé</Badge>
          <span className="font-medium">{outcome.fileName}</span>
          <span className="text-muted-foreground">même fichier que le relevé n° {outcome.existingStatementId}, rien n’a été créé</span>
        </li>
      );
    case 'unreadable':
      return (
        <li className="flex flex-wrap items-center gap-2">
          <Badge variant="destructive">Illisible</Badge>
          <span className="font-medium">{outcome.fileName}</span>
          <span className="text-destructive">{outcome.reason}</span>
        </li>
      );
  }
}

function CancelImportButton({ statement }: { statement: Statement }) {
  const [open, setOpen] = useState(false);
  const cancel = useCancelStatement();
  const created = statement.transactionsCreated;

  function confirm() {
    cancel.mutate(statement.id, {
      onSuccess: (result) => {
        setOpen(false);
        toast.success(`Import annulé : ${String(result.deletedTransactions)} écriture(s) supprimée(s)`);
      },
      onError: (error) => {
        setOpen(false);
        toast.error(apiErrorMessage(error, 'Annulation impossible'));
      },
    });
  }

  return (
    <>
      <IconActionButton label="Annuler l’import" variant="outline" onClick={() => setOpen(true)}>
        <Undo2 />
      </IconActionButton>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler l’import de « {statement.fileName} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              {statement.status === 'processed'
                ? `${String(created)} écriture(s) créée(s) par ce relevé seront supprimées, dont ${String(statement.transactionsValidated)} déjà validée(s). `
                : 'Ce relevé rejeté n’a créé aucune écriture. '}
              Le fichier pourra être redéposé ensuite. Les règles apprises sont conservées. Cette action est
              définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Conserver</AlertDialogCancel>
            <Button variant="destructive" onClick={confirm} disabled={cancel.isPending}>
              {cancel.isPending ? 'Annulation…' : 'Annuler l’import'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function StatementRow({ statement }: { statement: Statement }) {
  const processed = statement.status === 'processed';
  return (
    <TableRow>
      <TableCell className="max-w-xs truncate" title={statement.fileName}>
        {statement.fileName}
      </TableCell>
      <TableCell>
        {statement.periodStart !== null && statement.periodEnd !== null
          ? `${formatIsoDate(statement.periodStart)} → ${formatIsoDate(statement.periodEnd)}`
          : '—'}
      </TableCell>
      <TableCell className="text-right">
        {statement.openingBalanceCents === null || statement.closingBalanceCents === null
          ? '—'
          : `${formatCents(statement.openingBalanceCents, 'EUR')} → ${formatCents(statement.closingBalanceCents, 'EUR')}`}
      </TableCell>
      <TableCell className="text-right">
        {processed ? `${String(statement.transactionsCreated)} / ${String(statement.transactionsSkipped)}` : '—'}
      </TableCell>
      <TableCell>
        {processed ? (
          <Badge>Importé</Badge>
        ) : (
          <span className="flex flex-col gap-1">
            <Badge variant="destructive" className="w-fit">Rejeté</Badge>
            <span className="text-xs text-destructive">{statement.rejectionReason}</span>
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <CancelImportButton statement={statement} />
      </TableCell>
    </TableRow>
  );
}

export function StatementsPage() {
  const statements = useStatements();
  const { page, pageCount, start, end, setPage } = usePagination(statements.data?.length ?? 0);
  const deposit = useDepositStatements();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [results, setResults] = useState<StatementDepositOutcome[] | null>(null);

  const problems = [
    files.length > MAX_FILES ? `${String(MAX_FILES)} fichiers au plus par dépôt.` : null,
    ...files.filter((f) => !isAccepted(f)).map((f) => `${f.name} : seuls les PDF et les fichiers .md sont acceptés.`),
    ...files.filter((f) => f.size > MAX_BYTES).map((f) => `${f.name} : plus de 20 Mo.`),
  ].filter((message): message is string => message !== null);

  function handleDeposit() {
    if (files.length === 0 || problems.length > 0) return;
    deposit.mutate(files, {
      onSuccess: (response) => {
        setResults(response.results);
        setFiles([]);
        if (inputRef.current) inputRef.current.value = '';
        const imported = response.results.filter((r) => r.outcome === 'processed').length;
        const summary = `${String(imported)} relevé(s) importé(s) sur ${String(response.results.length)}`;
        if (imported === 0) toast.error(summary);
        else toast.success(summary);
      },
      onError: (error) => toast.error(apiErrorMessage(error, 'Échec du dépôt')),
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">Affectation</h1>
      <AssignmentTabs />

      <Alert>
        <AlertDescription>
          <ul className="list-disc space-y-1 pl-5">
            <li>Déposez des relevés au format PDF ou Markdown &quot;.md&quot;.</li>
            <li>
              Un relevé n’est importé que si &quot;solde initial + mouvements = solde final&quot; au centime près. Sinon il
              est rejeté en bloc et rien n’entre.
            </li>
            <li>
              Redéposer un même fichier ne crée rien, un relevé qui en chevauche un autre n’ajoute que les lignes
              nouvelles.
            </li>
            <li>
              Les écritures qui correspondent à une règle apprise sont validées d’office, les autres vont dans la{' '}
              <Link to="/affectation" className="underline">file d’attente</Link>.
            </li>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <IconActionButton
            label="Sélectionner des fichiers"
            variant="outline"
            onClick={() => inputRef.current?.click()}
          >
            <Upload />
          </IconActionButton>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.md,application/pdf,text/markdown"
            aria-label="Relevés à déposer"
            onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
            className="hidden"
          />
          <span className="text-sm text-muted-foreground">
            {files.length === 0 ? 'Aucun fichier choisi' : `${String(files.length)} fichier(s) sélectionné(s)`}
          </span>
        </div>
        {files.length > 0 && (
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            {files.map((file) => (
              <li key={`${file.name}-${String(file.size)}`}>
                {file.name} ({(file.size / 1024).toFixed(0)} Ko)
              </li>
            ))}
          </ul>
        )}
        {problems.map((message) => (
          <p key={message} className="text-sm text-destructive">{message}</p>
        ))}
        <div>
          <Button onClick={handleDeposit} disabled={files.length === 0 || problems.length > 0 || deposit.isPending}>
            {deposit.isPending ? 'Import en cours… (jusqu’à quelques secondes par PDF)' : `Déposer (${String(files.length)})`}
          </Button>
        </div>
      </div>

      {results !== null && (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-muted-foreground">Résultat du dépôt</h2>
          <ul className="flex flex-col gap-1.5 text-sm">
            {results.map((outcome, index) => (
              <OutcomeLine key={`${outcome.fileName}-${String(index)}`} outcome={outcome} />
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-medium text-muted-foreground">Relevés déjà déposés</h2>
        {statements.isPending && <p className="text-muted-foreground">Chargement…</p>}
        {statements.isError && <p className="text-destructive">Impossible de charger les relevés.</p>}
        {statements.data?.length === 0 && <p className="text-muted-foreground">Aucun relevé déposé.</p>}
        {statements.data && statements.data.length > 0 && (
          <Card className="gap-0 overflow-hidden py-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fichier</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead className="text-right">Solde initial → final</TableHead>
                  <TableHead className="text-right">Créées / déjà connues</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {statements.data.slice(start, end).map((statement) => (
                  <StatementRow key={statement.id} statement={statement} />
                ))}
              </TableBody>
            </Table>
            <TablePagination
              page={page}
              pageCount={pageCount}
              start={start}
              end={end}
              total={statements.data.length}
              onPageChange={setPage}
            />
          </Card>
        )}
      </div>
    </div>
  );
}
